import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { APP_NAME } from '../../shared/brand';
import { useSession } from '../api/hooks';
import { guestView } from '../guest';
import { PublicAboutPage } from '../pages/AboutPage';
import { SignInPage } from '../pages/SignInPage';
import { SplashPage } from '../pages/SplashPage';

/**
 * Nothing personal is requested until the session check says so. Local use needs no sign-in.
 * Signed-out visitors get the splash at / and About at /about. Any other URL stays on sign-in
 * so a deep link still opens the right page after login.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  // Keep showing the app through a failed background refetch; only a first-load failure blocks.
  if (!session.data) {
    if (!session.isError) return null;
    return (
      <main className="signin">
        <div className="card card--pad stack signin__card" role="alert">
          <h1 className="section-title">Can't reach {APP_NAME}</h1>
          <p className="small muted">{session.error.message}</p>
          <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} onClick={() => void session.refetch()}>
            Try again
          </button>
        </div>
      </main>
    );
  }
  if (session.data.required && !session.data.signedIn) {
    const view = guestView(location.pathname);
    if (view === 'splash') return <SplashPage />;
    if (view === 'about') return <PublicAboutPage />;
    return <SignInPage session={session.data} />;
  }
  return <>{children}</>;
}
